/* ============================================================
   ShishaTabak · global.js
   Menu toggle, age gate, cart drawer, free shipping bar, qty
   ============================================================ */
(function () {
  'use strict';

  const money = (cents) =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' })
      .format((cents || 0) / 100);

  /* ============================================================
     Gedeelde drawer-helpers (gebruikt door menu én cart)
     ============================================================ */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Scroll-lock met teller: meerdere panelen kunnen elkaar niet ontgrendelen.
  let scrollLocks = 0;
  function lockScroll() {
    if (scrollLocks === 0) document.body.classList.add('scroll-locked');
    scrollLocks++;
  }
  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.body.classList.remove('scroll-locked');
  }

  // Maakt een paneel onzichtbaar voor toetsenbord/screenreader als het dicht is.
  function setHidden(el, hidden) {
    if (!el) return;
    el.setAttribute('aria-hidden', String(hidden));
    if (hidden) el.setAttribute('inert', ''); else el.removeAttribute('inert');
  }

  // Houd Tab binnen het paneel zolang het open is. Geeft een cleanup-functie terug.
  function trapFocus(panel) {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    panel.addEventListener('keydown', handler);
    return () => panel.removeEventListener('keydown', handler);
  }

  /* ---------- Mobile menu ---------- */
  function initMobileMenu() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;
    const overlay = document.querySelector('[data-menu-overlay]');
    let lastFocus = null;
    let releaseTrap = null;

    function open() {
      lastFocus = document.activeElement;
      menu.classList.add('is-open');
      overlay?.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      setHidden(menu, false);
      lockScroll();
      releaseTrap = trapFocus(menu);
      (menu.querySelector('[data-menu-close]') || menu.querySelector(FOCUSABLE))?.focus();
    }
    function close() {
      if (!menu.classList.contains('is-open')) return;
      menu.classList.remove('is-open');
      overlay?.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      setHidden(menu, true);
      unlockScroll();
      releaseTrap?.(); releaseTrap = null;
      if (lastFocus && document.body.contains(lastFocus)) lastFocus.focus();
    }

    toggle.addEventListener('click', () => (menu.classList.contains('is-open') ? close() : open()));
    overlay?.addEventListener('click', close);
    menu.querySelector('[data-menu-close]')?.addEventListener('click', close);
    menu.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  /* ---------- Age gate ---------- */
  function initAgeGate() {
    const gate = document.querySelector('[data-age-gate]');
    if (!gate) return;
    const KEY = 'st-age-verified';
    try { if (localStorage.getItem(KEY) === 'yes') { gate.setAttribute('hidden', ''); return; } } catch (e) {}
    gate.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    gate.querySelector('[data-age-yes]')?.addEventListener('click', () => {
      try { localStorage.setItem(KEY, 'yes'); } catch (e) {}
      gate.setAttribute('hidden', '');
      document.body.style.overflow = '';
    });
    gate.querySelector('[data-age-no]')?.addEventListener('click', () => {
      window.location.href = 'https://www.google.com';
    });
  }

  /* ---------- Cart drawer ----------
     Gebruikt de officiële Cart AJAX API met de `sections`-parameter
     (Dawn-standaard): add/change geven de versgerenderde drawer-HTML terug. */
  const Cart = {
    drawer: null,
    overlay: null,
    sectionId: 'cart-drawer',
    lastFocus: null,
    releaseTrap: null,
    open() {
      if (!this.drawer || this.drawer.classList.contains('is-open')) return;
      this.lastFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      this.overlay?.classList.add('is-open');
      setHidden(this.drawer, false);
      lockScroll();
      this.releaseTrap = trapFocus(this.drawer);
      (this.drawer.querySelector('[data-cart-close]') || this.drawer.querySelector(FOCUSABLE))?.focus();
    },
    close() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.overlay?.classList.remove('is-open');
      setHidden(this.drawer, true);
      unlockScroll();
      this.releaseTrap?.(); this.releaseTrap = null;
      if (this.lastFocus && document.body.contains(this.lastFocus)) this.lastFocus.focus();
    },
    renderDrawer(html) {
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-inner]');
      const current = this.drawer?.querySelector('[data-cart-drawer-inner]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    },
    async updateCounts() {
      try {
        const cart = await (await fetch('/cart.js')).json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          // Alleen de badge in de header verbergen bij 0; het getal in de drawer-titel blijft staan.
          if (el.classList.contains('cart-count')) el.hidden = cart.item_count === 0;
        });
        this.updateFreeShipping(cart.total_price);
      } catch (e) {}
    },
    updateFreeShipping(total) {
      const threshold = parseInt(document.body.dataset.freeShippingThreshold || '0', 10);
      if (!threshold) return;
      document.querySelectorAll('[data-free-shipping-bar]').forEach((bar) => {
        const remaining = Math.max(0, threshold - total);
        const fill = bar.querySelector('[data-free-shipping-fill]');
        const msg = bar.querySelector('[data-free-shipping-msg]');
        if (fill) fill.style.width = Math.min(100, (total / threshold) * 100) + '%';
        if (msg) msg.textContent = remaining === 0
          ? (bar.dataset.msgReached || 'Je hebt gratis verzending! 🎉')
          : (bar.dataset.msgRemaining || 'Nog %amount% tot gratis verzending').replace('%amount%', money(remaining));
      });
    },
    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.overlay = document.querySelector('[data-cart-overlay]');
      document.querySelectorAll('[data-cart-open]').forEach((b) => b.addEventListener('click', (e) => {
        if (document.body.dataset.cartType === 'drawer') { e.preventDefault(); this.open(); }
      }));
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      this.updateCounts();

      // Alles binnen de drawer via delegatie: knoppen worden bij re-render vervangen,
      // maar deze ene listener op de drawer-root blijft bestaan.
      this.drawer?.addEventListener('click', async (e) => {
        const closeBtn = e.target.closest('[data-cart-close]');
        const setBtn = e.target.closest('[data-qty-set]');
        const remove = e.target.closest('[data-cart-remove]');
        if (closeBtn) { e.preventDefault(); this.close(); }
        else if (setBtn) { e.preventDefault(); setBtn.disabled = true; await this.updateLine(setBtn.dataset.line, parseInt(setBtn.dataset.qtySet, 10)); }
        else if (remove) { e.preventDefault(); await this.updateLine(remove.dataset.line, 0); }
      });
    },
    async updateLine(line, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ line: parseInt(line, 10), quantity: parseInt(quantity, 10), sections: this.sectionId, sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (data && data.sections) this.renderDrawer(data.sections[this.sectionId]);
        this.updateCounts();
      } catch (e) { console.error(e); }
    },
    async addFromForm(form) {
      const fd = new FormData(form);
      fd.append('sections', this.sectionId);
      fd.append('sections_url', window.location.pathname);
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: fd });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.description || 'Er ging iets mis.'); }
      const data = await res.json();
      if (data.sections) this.renderDrawer(data.sections[this.sectionId]);
      this.updateCounts();
    }
  };

  /* ---------- Add to cart (AJAX) ---------- */
  function initAddToCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action$="/cart/add"]');
      if (!form || document.body.dataset.cartType !== 'drawer') return;
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      btn && (btn.disabled = true);
      try {
        await Cart.addFromForm(form);
        Cart.open();
      } catch (err) {
        alert(err.message || 'Er ging iets mis.');
      } finally { btn && (btn.disabled = false); }
    });
  }

  /* ---------- Quantity steppers (product page) ---------- */
  function initQuantity() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-step]');
      if (!btn) return;
      const input = btn.parentElement.querySelector('input[type="number"]');
      if (!input) return;
      const step = parseInt(btn.dataset.qtyStep, 10);
      input.value = Math.max(parseInt(input.min || '1', 10), (parseInt(input.value, 10) || 1) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- Product media gallery (afbeelding + video) ---------- */
  function initGallery() {
    document.querySelectorAll('[data-gallery]').forEach((gallery) => {
      const thumbs = gallery.querySelectorAll('[data-thumb]');
      const stages = gallery.querySelectorAll('[data-stage-item]');
      if (!thumbs.length) return;
      function activate(id) {
        stages.forEach((s) => {
          const on = s.dataset.mediaId === id;
          s.classList.toggle('is-active', on);
          if (!on) { const v = s.querySelector('video'); if (v) v.pause(); }
        });
        thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.mediaId === id));
      }
      thumbs.forEach((t) => t.addEventListener('click', () => activate(t.dataset.mediaId)));
      gallery._activate = activate;
    });
  }

  /* ---------- Sticky buy bar (CRO, mobiel) ---------- */
  function initStickyBar() {
    const bar = document.querySelector('[data-sticky-bar]');
    const anchor = document.querySelector('[data-buybox]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const belowFold = !en.isIntersecting && en.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', belowFold);
        bar.setAttribute('aria-hidden', belowFold ? 'false' : 'true');
      });
    }, { threshold: 0 });
    io.observe(anchor);
    bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
      const form = document.querySelector('form[action$="/cart/add"]');
      if (!form) return;
      if (form.requestSubmit) form.requestSubmit(); else form.submit();
    });
  }

  /* ---------- Variant picker ---------- */
  function initVariantPicker() {
    const root = document.querySelector('[data-product]');
    const dataEl = document.querySelector('[data-product-json]');
    if (!root || !dataEl) return;
    let variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

    const idInput = root.querySelector('[data-variant-id]');
    const priceEl = root.querySelector('[data-price]');
    const addBtn = root.querySelector('[data-add-to-cart]');
    const addText = root.querySelector('[data-add-text]');
    const gallery = root.querySelector('[data-gallery]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const stickyAdd = document.querySelector('[data-sticky-add]');
    const labels = {
      add: (addBtn && addBtn.dataset.addLabel) || 'In winkelwagen',
      sold: (addBtn && addBtn.dataset.soldoutLabel) || 'Uitverkocht',
      unavail: (addBtn && addBtn.dataset.unavailableLabel) || 'Niet beschikbaar'
    };

    function selectedOptions() {
      return Array.from(root.querySelectorAll('[data-option-input]:checked')).map((i) => i.value);
    }
    function update() {
      const opts = selectedOptions();
      const variant = variants.find((v) => v.options.every((o, i) => o === opts[i]));
      if (!variant) { addBtn.disabled = true; addText.textContent = labels.unavail; if (stickyAdd) stickyAdd.disabled = true; return; }
      idInput.value = variant.id;
      // price
      if (priceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceEl.innerHTML = '<span class="price price--on-sale"><s class="price__regular">' + money(variant.compare_at_price) + '</s> <span class="price__sale">' + money(variant.price) + '</span></span>';
        } else {
          priceEl.innerHTML = '<span class="price">' + money(variant.price) + '</span>';
        }
      }
      // variant-media tonen (zoals een standaard Shopify-thema)
      if (variant.featured_media && gallery && gallery._activate) {
        gallery._activate(String(variant.featured_media.id));
      }
      // sticky prijs spiegelen
      if (stickyPrice && priceEl) stickyPrice.innerHTML = priceEl.innerHTML;
      // availability
      addBtn.disabled = !variant.available;
      addText.textContent = variant.available ? labels.add : labels.sold;
      if (stickyAdd) stickyAdd.disabled = !variant.available;
      // update URL without reload
      if (history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }
    root.querySelectorAll('[data-option-input]').forEach((i) => i.addEventListener('change', update));
  }

  /* ---------- Generieke carrousel ---------- */
  function initCarousels(scope) {
    (scope || document).querySelectorAll('[data-carousel]').forEach((root) => {
      const track = root.querySelector('[data-carousel-track]');
      if (!track || root.dataset.carouselReady) return;
      root.dataset.carouselReady = '1';
      const prev = root.querySelector('[data-carousel-prev]');
      const next = root.querySelector('[data-carousel-next]');
      const step = () => Math.max(track.clientWidth, 100);
      const update = () => { if (!prev || !next) return; const max = track.scrollWidth - track.clientWidth - 2; prev.disabled = track.scrollLeft <= 2; next.disabled = track.scrollLeft >= max; };
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Dynamische productaanbevelingen (Shopify API) ---------- */
  function initProductRecs() {
    if (window.Shopify && window.Shopify.designMode) return; // editor: server-side placeholders tonen
    document.querySelectorAll('[data-product-recs]').forEach((root) => {
      const url = root.dataset.url;
      if (!url) return;
      fetch(url).then((r) => r.text()).then((text) => {
        const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-product-recs-inner]');
        const inner = root.querySelector('[data-product-recs-inner]');
        if (fresh && inner && fresh.querySelector('[data-rec-item]')) {
          inner.innerHTML = fresh.innerHTML;
          // De carrousel-root kan al 'ready' zijn gemarkeerd (server-side recs op de
          // eerste render). Reset de vlag zodat de pijlen aan de verse track binden.
          const carousel = root.closest('[data-carousel]');
          if (carousel) delete carousel.dataset.carouselReady;
          initCarousels(document);
        } else {
          const sec = root.closest('.shopify-section');
          if (sec) sec.style.display = 'none';
        }
      }).catch(() => {
        const sec = root.closest('.shopify-section');
        if (sec) sec.style.display = 'none';
      });
    });
  }

  /* ---------- Predictive search (header) ----------
     Gebruikt de Predictive Search JSON-API (/search/suggest.json) en rendert
     client-side. Werkt onder `shopify theme dev` én live (geen Section Rendering,
     dus geen 401 bij wachtwoord-beveiligde stores). */
  function initPredictiveSearch() {
    const root = document.querySelector('[data-predictive-search]');
    if (!root) return;
    const input = root.querySelector('[data-predictive-input]');
    const results = root.querySelector('[data-predictive-results]');
    const clearBtn = root.querySelector('[data-predictive-clear]');
    if (!input || !results) return;

    const base = root.dataset.searchUrl || '/search/suggest';
    const tNoResults = root.dataset.noResults || 'Geen resultaten voor "%terms%".';
    const tAll = root.dataset.allResults || 'Alle resultaten bekijken';
    const tFrom = root.dataset.fromPrice || 'vanaf %price%';
    const searchPage = (root.querySelector('form') && root.querySelector('form').getAttribute('action')) || '/search';

    let timer = null, controller = null, active = -1;

    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const rows = () => Array.from(results.querySelectorAll('.predictive__row'));
    function open() { results.hidden = false; input.setAttribute('aria-expanded', 'true'); }
    function close() { results.hidden = true; input.setAttribute('aria-expanded', 'false'); active = -1; }
    function setActive(i) {
      const list = rows();
      list.forEach((el) => el.classList.remove('is-active'));
      if (i >= 0 && i < list.length) {
        list[i].classList.add('is-active');
        list[i].scrollIntoView({ block: 'nearest' });
        active = i;
      } else { active = -1; }
    }

    function priceLabel(p) {
      const raw = (p.price != null && p.price !== '') ? p.price : p.price_min;
      if (raw == null || raw === '') return '';
      const cents = Math.round(parseFloat(raw) * 100);
      if (isNaN(cents)) return '';
      const m = money(cents);
      return p.price_varies ? tFrom.replace('%price%', m) : m;
    }

    function render(products, q) {
      if (!products.length) {
        results.innerHTML = '<div class="predictive"><p class="predictive__empty">' +
          esc(tNoResults.replace('%terms%', q)) + '</p></div>';
        open(); return;
      }
      let items = '';
      products.forEach((p) => {
        const src = p.image || (p.featured_image && p.featured_image.url) || '';
        const imgUrl = src ? src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=120' : '';
        const media = imgUrl ? '<img src="' + esc(imgUrl) + '" width="56" height="56" loading="lazy" alt="">' : '';
        items += '<li class="predictive__row" role="option">' +
          '<a href="' + esc(p.url) + '" class="predictive__item" tabindex="-1">' +
            '<span class="predictive__media">' + media + '</span>' +
            '<span class="predictive__text">' +
              '<span class="predictive__title">' + esc(p.title) + '</span>' +
              '<span class="predictive__price">' + esc(priceLabel(p)) + '</span>' +
            '</span>' +
          '</a></li>';
      });
      const allLink = '<a href="' + esc(searchPage) + '?q=' + encodeURIComponent(q) +
        '" class="predictive__all">' + esc(tAll) +
        ' <svg class="icon" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></a>';
      results.innerHTML = '<div class="predictive"><ul class="predictive__list" role="listbox">' + items + '</ul>' + allLink + '</div>';
      active = -1;
      open();
    }

    async function run(q) {
      if (controller) controller.abort();
      controller = new AbortController();
      try {
        const url = base + '.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
        const res = await fetch(url, { signal: controller.signal, headers: { 'Accept': 'application/json' } });
        if (!res.ok) return;
        const data = await res.json();
        const products = (((data || {}).resources || {}).results || {}).products || [];
        render(products, q);
      } catch (e) { /* afgebroken of netwerkfout: stil */ }
    }

    input.addEventListener('input', () => {
      const q = input.value.trim();
      if (clearBtn) clearBtn.hidden = q.length === 0;
      clearTimeout(timer);
      if (q.length < 2) { close(); return; }
      timer = setTimeout(() => run(q), 180);
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && results.innerHTML.trim()) open();
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); return; }
      if (results.hidden) return;
      const list = rows();
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(Math.min(active + 1, list.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(active - 1, -1)); }
      else if (e.key === 'Enter' && active >= 0 && list[active]) {
        const link = list[active].querySelector('a');
        if (link) { e.preventDefault(); window.location.href = link.href; }
      }
    });
    clearBtn && clearBtn.addEventListener('click', () => {
      input.value = ''; clearBtn.hidden = true; close(); input.focus();
    });
    document.addEventListener('click', (e) => { if (!root.contains(e.target)) close(); });
  }

  /* ---------- Taal/valuta-dropdown sluiten bij klik buiten / Escape ---------- */
  function initLocalizationClose() {
    const loc = document.querySelector('.localization__details');
    if (!loc) return;
    document.addEventListener('click', (e) => { if (!loc.contains(e.target)) loc.removeAttribute('open'); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') loc.removeAttribute('open'); });
  }

  /* ---------- Cookievoorkeuren (Shopify consent) ---------- */
  function initCookiePrefs() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-cookie-prefs]')) return;
      e.preventDefault();
      if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
        window.privacyBanner.showPreferences();
      } else if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.openConsentManager === 'function') {
        window.Shopify.customerPrivacy.openConsentManager();
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initMobileMenu();
    initAgeGate();
    Cart.init();
    initAddToCart();
    initQuantity();
    initGallery();
    initVariantPicker();
    initStickyBar();
    initCarousels(document);
    initProductRecs();
    initPredictiveSearch();
    initLocalizationClose();
    initCookiePrefs();
  });

  window.STCart = Cart;
})();
